import { useEffect } from "react";

/**
 * Makes every data table in the app readable on a phone.
 *
 * Below 640px the shared stylesheet turns each table row into a card: the
 * header row is hidden and each cell becomes a "Label — value" line. That needs
 * every <td> to carry data-label with its column name, which cannot be written
 * by hand here — the app has 100+ tables and almost all of them build their
 * rows inside .map(), often with columns that vary by permission or filter.
 *
 * So the labels are stamped at runtime from each table's own <thead>. One
 * observer, mounted once from App.jsx, covers every table including any added
 * later. It does nothing at all on desktop.
 *
 * Cells that span columns (an empty state, a totals row) are skipped — there is
 * no single column they belong to.
 */
const TABLE_SELECTOR = "table.tbl, table.data-table, table.rtable, table.sa-table, table.req-table";
const MOBILE = "(max-width: 640px)";

function stamp(root = document) {
  for (const table of root.querySelectorAll(TABLE_SELECTOR)) {
    const heads = [...table.querySelectorAll("thead th")].map((th) => th.textContent.trim());
    if (!heads.length) continue;
    for (const row of table.querySelectorAll("tbody tr")) {
      const cells = row.children;
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        if (cell.tagName !== "TD") continue;
        if (cell.colSpan > 1) { cell.removeAttribute("data-label"); continue; }
        const label = heads[i] || "";
        if (label && cell.getAttribute("data-label") !== label) cell.setAttribute("data-label", label);
      }
    }
  }
}

export default function ResponsiveTables() {
  useEffect(() => {
    const mql = window.matchMedia(MOBILE);
    let observer = null;
    let queued = false;

    const run = () => {
      queued = false;
      stamp();
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(run);
    };

    const attach = () => {
      if (observer) return;
      stamp();
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true });
    };
    const detach = () => {
      observer?.disconnect();
      observer = null;
    };

    const onChange = (e) => (e.matches ? attach() : detach());
    if (mql.matches) attach();
    mql.addEventListener("change", onChange);

    return () => {
      mql.removeEventListener("change", onChange);
      detach();
    };
  }, []);

  return null;
}
